import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, ChevronDown, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useNotifications } from '@/contexts/NotificationContext';
import { formatMetricNumber } from '@/hooks/usePerformance';
import { formatDecimal, formatInteger, formatSignedPercent, numberLocale } from '@/lib/language';
import {
  useFeedExperiment,
  type ExperimentEligibility,
  type ExperimentGate,
  type ExperimentResults,
} from '@/hooks/useFeedExperiment';

interface ExperimentCardProps {
  feedId: string | null;
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Aktive App-Sprache, durchgereicht wie `t` (Design-Audit F18).
 *
 * Bewusst als Prop und nicht per eigenem `useTranslation` in jedem Bauteil:
 * die Zahlenformatierung muss garantiert dieselbe Sprache treffen wie die
 * Saetze, in die sie eingesetzt wird. Genau das war der Befund — "729,5 %"
 * im Satz und "729.5" in der Zahl daneben.
 */
type Lang = string | null | undefined;

/**
 * Farbe des Effekt-Werts, aus dem VORZEICHEN.
 *
 * Exportiert, damit der Vertrag pruefbar ist: die Karte haengt an zwei
 * Supabase-RPCs und wird im Seiten-Sweep herausgemockt, ihr Farbverhalten war
 * damit strukturell unbeobachtet — dieselbe Lage, in der der Stabilitaets-Chip
 * jahrelang eine Verschlechterung gruen faerbte. Der Aufruf am Wert
 * (`liftColorClass(results.lift_rel_pct)`) wird in
 * `ExperimentCard.liftColor.test.ts` zusaetzlich am Quelltext festgenagelt,
 * sonst kann die Funktion richtig sein und trotzdem an der falschen Zahl haengen.
 *
 * Beide Seiten tragen `-ink`: `text-destructive` als Schrift kam auf 3.78:1
 * gegen `--card` und verfehlte damit AA, waehrend die gruene Seite bei 6.42:1
 * lag. Erzwungen ist das fuer die vier vorzeichenfuehrenden Flaechen der
 * Wirkungs-Seite (`PerformanceSigKpiCards`, `StabilityChip`, diese Karte,
 * `PerformanceDashboard`) — der statische Scan in
 * `directionHonesty.sweep.test.tsx` laeuft nur ueber `IMPACT_SURFACE` und sagt
 * ueber den Rest des Projekts nichts. Ausserhalb dieser Flaechen faerbt das
 * Flaechen-Token weiterhin Schrift (`UsageWidget`, `JobHealthIndicator`,
 * `ui/form` u.a.). Nachgerechnet in `src/lib/contrast.test.ts`.
 */
export function liftColorClass(v: number | null): string {
  if (v === null) return 'text-foreground';
  if (v > 0) return 'text-success-ink';
  if (v < 0) return 'text-destructive-ink';
  return 'text-foreground';
}

/** Numeric day-in-test, clamped to the planned duration so a stale/late render
 * never shows "day 31 of 28". Duration is derived from planned_end_at minus
 * started_at (the table has a duration_days column, but the hook doesn't select it). */
function dayInTest(startedAt: string, plannedEndAt: string): { day: number; total: number } {
  const start = new Date(startedAt).getTime();
  const end = new Date(plannedEndAt).getTime();
  const total = Math.max(1, Math.round((end - start) / 86400000));
  const elapsed = Math.floor((Date.now() - start) / 86400000) + 1;
  return { day: Math.min(total, Math.max(1, elapsed)), total };
}

/** Klicks pro Produkt ist die Primaermetrik. Drei Nachkommastellen, weil die
 * Werte im Longtail typischerweise deutlich unter 1 liegen. */
function formatClicksPerProduct(v: number | null | undefined, lang: Lang): string {
  return formatDecimal(v, lang, 3);
}

function formatCtr(v: number | null | undefined, lang: Lang): string {
  if (v === null || v === undefined) return '—';
  return `${formatDecimal(v, lang, 2)}%`;
}

/** `formatMetricNumber` ruft `toLocaleString` und wirft damit bei null. Die
 * Arm-Klicks sind waehrend der Laufzeit bewusst NULL, deshalb hier ein
 * Platzhalter statt eines weissen Bildschirms. */
function formatMetricOrDash(v: number | null | undefined, lang: Lang): string {
  if (v === null || v === undefined) return '—';
  return formatMetricNumber(v, numberLocale(lang));
}

/** Bekannte Gruende bekommen Klartext, unbekannte den Rohcode. Ein stiller
 * Fallback waere hier schlimmer als ein haesslicher String: der Grund entscheidet,
 * ob das Ergebnis ueberhaupt verwendet werden darf.
 *
 * Die Schluessel sind die WOERTLICHEN Codes aus `get_experiment_results`
 * (Migration 20260726234000, `v_expl_reasons`) — exportiert, damit der Test
 * sie gegen die dokumentierten RPC-Codes pinnen kann. `ended_early` statt
 * `ended_before_planned_end` hat die fertige Uebersetzung fuer den einzigen
 * vom Nutzer selbst ausloesbaren Grund unerreichbar gemacht. */
export const EXPLORATORY_REASON_KEYS: Record<string, string> = {
  attrition_total_gt_20: 'experiment.results.exploratoryReason.attritionTotal',
  window_days_missing_gt_3: 'experiment.results.exploratoryReason.windowGaps',
  ended_before_planned_end: 'experiment.results.exploratoryReason.endedEarly',
};

function exploratoryReasonText(code: string, t: TFn): string {
  const key = EXPLORATORY_REASON_KEYS[code];
  return key ? t(key) : t('experiment.results.exploratoryReason.unknown', { code });
}

/** Ein exploratorisches Ergebnis darf nie wie ein Nachweis aussehen. Die Gruende
 * stehen dabei, weil "exploratorisch" ohne Begruendung als Floskel gelesen wird
 * und dann genau nichts bewirkt. */
function ExploratoryNotice({ results, t }: { results: ExperimentResults; t: TFn }) {
  if (!results.result_exploratory) return null;
  const reasons = results.exploratory_reasons ?? [];
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-foreground">{t('experiment.results.exploratoryTitle')}</p>
      {reasons.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
          {reasons.map((code) => (
            <li key={code}>{exploratoryReasonText(code, t)}</li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">{t('experiment.results.exploratoryHint')}</p>
    </div>
  );
}

/** G4. Ein nicht aktives Merchant Center entwertet alles, was im Fenster gemessen
 * wurde, das gehoert nach oben und nicht in eine Fussnote. */
function McAccessNotice({ results, t }: { results: ExperimentResults; t: TFn }) {
  if (results.mc_access_active !== false) return null;
  return <p className="text-xs font-medium text-destructive-ink">{t('experiment.results.mcAccessRevoked')}</p>;
}

/** Ein Arm der Primaermetrik: Klicks pro Produkt gross, absolute Klicks und
 * Produktanzahl klein darunter. */
function ArmPrimary({ label, clicksPerProduct, clicks, products, t, lang }: {
  label: string;
  clicksPerProduct: number | null;
  clicks: number | null;
  products: number;
  t: TFn;
  lang: Lang;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-lg font-bold tabular-nums leading-tight">{formatClicksPerProduct(clicksPerProduct, lang)}</p>
      <p className="text-xs text-muted-foreground">{t('experiment.results.clicksPerProduct')}</p>
      <p className="text-xs text-muted-foreground">
        {t('experiment.results.armDetail', {
          clicks: formatMetricOrDash(clicks, lang),
          products: formatMetricOrDash(products, lang),
        })}
      </p>
    </div>
  );
}

/** Fortschritt waehrend der Laufzeit, strikt armblind.
 *
 * Frueher standen hier die Klicks je Arm nebeneinander. Das war das Leck: wer
 * taeglich hinschaut und beim ersten guten Zwischenstand beendet, macht aus einem
 * 5-Prozent-Test einen mit rund 19 bis 20 Prozent Fehlalarmrate. Die RPC liefert
 * die Arm-Klicks waehrend `running` deshalb gar nicht mehr aus.
 *
 * Was hier steht, ist entweder ueber beide Arme gepoolt (Klicks, G1) oder eine
 * reine Zaehlung (Auslieferung G2, Schwund G3, Kontostatus G4). Keine dieser
 * Groessen verraet den Armunterschied, und alle vier sind Guardrails, die man
 * nicht 28 Tage lang ignorieren darf. */
function RunningProgress({ results, startedAt, plannedEndAt, t, lang }: {
  results: ExperimentResults | null;
  startedAt: string;
  plannedEndAt: string;
  t: TFn;
  lang: Lang;
}) {
  const evaluationDate = new Date(plannedEndAt).toLocaleDateString(numberLocale(lang));

  if (!results) {
    return <p className="text-sm text-muted-foreground">{t('experiment.running.collecting')}</p>;
  }

  const { day, total } = dayInTest(startedAt, plannedEndAt);
  const withData = results.window_days_with_data ?? null;

  const delivery = results.delivery_rate_pct ?? null;
  const deliveryFailed = delivery !== null && delivery < 95;

  const diffPp = results.attrition_diff_pp ?? null;
  // Fallback deckt das Deploy-Fenster ab, in dem das Frontend schon neu ist und
  // die RPC noch nicht: dann lieber selbst rechnen als das Gate verschweigen.
  const diffPassed = results.attrition_diff_passed ?? (diffPp === null || diffPp <= 1);

  return (
    <div className="space-y-3">
      {/* G1-Basis. Beide Arme zusammen, damit die Zahl nichts ueber den
        * Unterschied verraet, aber trotzdem sichtbar wird, wenn der Feed
        * insgesamt einbricht (Massendisapproval, Budgetstopp, Feed-Ausfall). */}
      <div className="space-y-1">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {t('experiment.running.pooledTitle')}
        </p>
        <p className="text-lg font-bold tabular-nums leading-tight">
          {formatMetricOrDash(results.pooled_clicks, lang)}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('experiment.running.pooledDetail', {
            perProduct: formatClicksPerProduct(results.pooled_clicks_per_product, lang),
          })}
        </p>
      </div>

      <p className="text-xs text-muted-foreground">
        {withData === null
          ? t('experiment.running.day', { day, total })
          : t('experiment.running.windowProgress', { day, total, withData })}
      </p>

      <McAccessNotice results={results} t={t} />
      <ExploratoryNotice results={results} t={t} />

      {/* G2. Wird immer ausgewiesen, auch bei 100 Prozent: ein Wert, der nur bei
        * Problemen erscheint, laesst offen, ob er geprueft wurde oder gerade nur
        * nicht auffaellt. Realer Fall: Upload-Job mit 3.175 von 3.275 Produkten,
        * der erst nach 28 Tagen als Nullergebnis aufgefallen waere. */}
      <p className={`text-xs ${deliveryFailed ? 'font-medium text-destructive-ink' : 'text-muted-foreground'}`}>
        {delivery === null
          ? t('experiment.results.deliveryUnknown')
          : t('experiment.results.delivery', { rate: formatDecimal(delivery, lang) })}
        {deliveryFailed ? ` ${t('experiment.running.deliveryWarning')}` : ''}
      </p>

      {/* G3 in Stueckzahlen statt Prozent: Zaehlungen sind waehrend der Laufzeit
        * ausdruecklich erlaubt, Klicks nicht. Der Sync loescht real 30 bis 41
        * Prozent der Produkte je 28 Tage, das darf nicht erst am Ende auffallen. */}
      <p className={`text-xs ${diffPassed ? 'text-muted-foreground' : 'font-medium text-destructive-ink'}`}>
        {t('experiment.running.attritionCounts', {
          treatmentPresent: formatCount(results.n_treatment_present, lang),
          treatmentAssigned: formatCount(results.n_treatment_assigned, lang),
          controlPresent: formatCount(results.n_control_present, lang),
          controlAssigned: formatCount(results.n_control_assigned, lang),
        })}
        {diffPp !== null ? ` ${t('experiment.results.attritionDiff', { diff: formatDecimal(diffPp, lang) })}` : ''}
        {!diffPassed ? ` ${t('experiment.results.attritionDiffFailed')}` : ''}
      </p>

      <p className="text-sm">{t('experiment.running.resultsFrom', { date: evaluationDate })}</p>
      <p className="text-xs text-muted-foreground">{t('experiment.running.blindHint')}</p>
    </div>
  );
}

/** Auslieferung (G2), Schwund (G3), Kontostatus (G4) und SRM.
 *
 * Der Block rendert IMMER, sobald Ergebnisse vorliegen. Frueher verschwand er,
 * sobald alles in Ordnung war, und genau das ist der Fehler: ein Wert, der nur
 * bei Problemen erscheint, laesst offen, ob er geprueft wurde oder gerade nur
 * nicht auffaellt. Der Analyseplan verlangt Auslieferung "in jedem Fall, auch wenn
 * er 100 Prozent betraegt" und Schwund "immer, auch bei null".
 *
 * Ruhige Darstellung wenn alles passt, deutliche sobald eine Schwelle reisst. */
function IntegritySummary({ results, t, lang }: {
  results: ExperimentResults;
  t: TFn;
  lang: Lang;
}) {
  const delivery = results.delivery_rate_pct ?? null;
  const deliveryFailed = delivery !== null && delivery < 95;

  const attritionTreatment = results.attrition_treatment_pct ?? 0;
  const attritionControl = results.attrition_control_pct ?? 0;
  const attritionTotalFailed = Math.max(attritionTreatment, attritionControl) > 20;

  const diffPp = results.attrition_diff_pp ?? null;
  // Fallback deckt das Deploy-Fenster ab, in dem das Frontend schon neu ist und
  // die RPC noch nicht: dann lieber selbst rechnen als das Gate verschweigen.
  const diffPassed = results.attrition_diff_passed ?? (diffPp === null || diffPp <= 1);
  const attritionFailed = !diffPassed || attritionTotalFailed;

  const breached =
    !results.srm_passed
    || deliveryFailed
    || attritionFailed
    || results.mc_access_active === false
    || results.result_exploratory === true;

  return (
    <div
      className={`space-y-1 rounded-md border p-2 ${
        breached ? 'border-warning/40 bg-warning/5' : 'border-border bg-muted/30'
      }`}
    >
      <ExploratoryNotice results={results} t={t} />
      <McAccessNotice results={results} t={t} />

      {!results.srm_passed && (
        <p className="text-xs font-medium text-foreground">{t('experiment.results.srmFailed')}</p>
      )}

      <p className={`text-xs ${deliveryFailed ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
        {delivery === null
          ? t('experiment.results.deliveryUnknown')
          : t('experiment.results.delivery', { rate: formatDecimal(delivery, lang) })}
        {deliveryFailed ? ` ${t('experiment.results.deliveryFailed')}` : ''}
      </p>

      <p className={`text-xs ${attritionFailed ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
        {t('experiment.results.attrition', {
          treatment: formatDecimal(attritionTreatment, lang),
          control: formatDecimal(attritionControl, lang),
        })}
        {diffPp !== null ? ` ${t('experiment.results.attritionDiff', { diff: formatDecimal(diffPp, lang) })}` : ''}
        {!diffPassed ? ` ${t('experiment.results.attritionDiffFailed')}` : ''}
        {attritionTotalFailed ? ` ${t('experiment.results.attritionTotalFailed')}` : ''}
      </p>
    </div>
  );
}

/** Auswertungsfenster. Kalendertage und Tage mit Daten laufen auseinander, sobald
 * ein Performance-Pull ausgefallen ist. Sind beide gleich, reicht eine Zahl. */
function WindowLine({ results, t }: {
  results: ExperimentResults;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  if (!results.window_days || results.window_days <= 0) return null;
  // Fallback deckt den Moment ab, in dem das Frontend schon neu ist und die RPC
  // noch nicht: dann lieber eine Zahl zeigen als "undefined mit Daten".
  const withData = results.window_days_with_data ?? results.window_days;
  const complete = withData >= results.window_days;
  return (
    <p className="text-xs text-muted-foreground">
      {complete
        ? t('experiment.results.window', { days: results.window_days })
        : t('experiment.results.windowWithGaps', { days: results.window_days, withData })}
    </p>
  );
}

/** Shared treatment/control comparison block — used both while a test is
 * still running (once gates_passed flips true) and once it's completed.
 * Primary metric is clicks per product (paired, CUPED-adjusted); CTR and
 * impressions are secondary diagnostics only, because Google reports
 * per-product impressions only above a reporting threshold. */
function ResultsSummary({ results, t, lang }: { results: ExperimentResults; t: TFn; lang: Lang }) {
  if (!results.gates_passed) {
    // Warum die Gates gerissen sind, ist fuer die Einordnung wichtiger als die
    // Tatsache selbst: zu wenig Daten ist harmlos, ein SRM ist ein Defekt.
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">{t('experiment.results.gatesFailed')}</p>
        <WindowLine results={results} t={t} />
        <IntegritySummary results={results} t={t} lang={lang} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <IntegritySummary results={results} t={t} lang={lang} />
      <div className="grid grid-cols-2 gap-4 text-sm">
        <ArmPrimary
          label={t('experiment.results.treatment')}
          clicksPerProduct={results.treatment_clicks_per_product}
          clicks={results.treatment_clicks}
          products={results.n_treatment}
          t={t}
          lang={lang}
        />
        <ArmPrimary
          label={t('experiment.results.control')}
          clicksPerProduct={results.control_clicks_per_product}
          clicks={results.control_clicks}
          products={results.n_control}
          t={t}
          lang={lang}
        />
      </div>

      <div className={`text-sm font-semibold ${liftColorClass(results.lift_rel_pct)}`}>
        {t('experiment.results.lift')}: {formatSignedPercent(results.lift_rel_pct, lang)}
      </div>

      {results.ci_rel_lower_pct !== null && results.ci_rel_upper_pct !== null && (
        <p className="text-xs text-muted-foreground font-mono">
          {t('experiment.results.ci', {
            lower: formatDecimal(results.ci_rel_lower_pct, lang),
            upper: formatDecimal(results.ci_rel_upper_pct, lang),
          })}
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        {results.is_significant ? t('experiment.results.significant') : t('experiment.results.notSignificant')}
      </p>

      {results.n_pairs > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('experiment.results.pairs', { pairs: formatMetricNumber(results.n_pairs, numberLocale(lang)) })}
        </p>
      )}

      <WindowLine results={results} t={t} />

      <div className="space-y-1 border-t border-border pt-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {t('experiment.results.secondaryTitle')}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">{t('experiment.results.treatment')}</p>
            <p className="text-xs text-muted-foreground tabular-nums">
              {t('experiment.results.armSecondary', {
                ctr: formatCtr(results.treatment_ctr_pct, lang),
                impressions: formatMetricOrDash(results.treatment_impressions, lang),
              })}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t('experiment.results.control')}</p>
            <p className="text-xs text-muted-foreground tabular-nums">
              {t('experiment.results.armSecondary', {
                ctr: formatCtr(results.control_ctr_pct, lang),
                impressions: formatMetricOrDash(results.control_impressions, lang),
              })}
            </p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{t('experiment.results.secondaryHint')}</p>
      </div>
    </div>
  );
}

export function ExperimentCard({ feedId }: ExperimentCardProps) {
  const { t, i18n } = useTranslation('performance');
  const lang = i18n?.language;
  const navigate = useNavigate();
  const { toast } = useNotifications();
  const [methodOpen, setMethodOpen] = useState(false);

  const {
    eligibility,
    eligibilityLoading,
    autoUploadEnabled,
    latestExperiment,
    results,
    startExperiment,
    isStarting,
    completeExperiment,
    cancelExperiment,
  } = useFeedExperiment(feedId);

  // Zusatz-Feature, kein Skeleton-Geflacker: solange die Eligibility noch
  // laedt UND kein vorheriges Experiment existiert, hat die Karte nichts zu
  // sagen (Step 4 des Task-5-Briefs).
  if (eligibilityLoading && !latestExperiment) return null;

  const handleStart = async () => {
    try {
      await startExperiment();
    } catch {
      toast({ title: t('experiment.actionError'), variant: 'destructive' });
    }
  };

  const handleComplete = async () => {
    if (!latestExperiment) return;
    try {
      await completeExperiment(latestExperiment.id);
    } catch {
      toast({ title: t('experiment.actionError'), variant: 'destructive' });
    }
  };

  const handleCancel = async () => {
    if (!latestExperiment) return;
    try {
      await cancelExperiment(latestExperiment.id);
    } catch {
      toast({ title: t('experiment.actionError'), variant: 'destructive' });
    }
  };

  let body: ReactNode;

  if (latestExperiment?.status === 'running') {
    const { day, total } = dayInTest(latestExperiment.started_at, latestExperiment.planned_end_at);

    body = (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">{t('experiment.running.title')}</p>
            <p className="text-xs text-muted-foreground">{t('experiment.running.day', { day, total })}</p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">{t('experiment.running.cancelCta')}</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('experiment.running.cancelCta')}</AlertDialogTitle>
                <AlertDialogDescription>{t('experiment.running.cancelConfirm')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('experiment.start.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleCancel}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {t('experiment.running.cancelCta')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Waehrend der Laufzeit gibt es bewusst KEINE Effektgroessen und keine
          * Zahl je Arm zu sehen. Die RPC liefert beides gar nicht erst: Lift, z,
          * CI und Signifikanz sind NULL, und seit dem Analyseplan auch die
          * Klicks, Impressionen, Klicks pro Produkt und CTR je Arm. Wer taeglich
          * schaut und beim ersten gruenen Zwischenstand stoppt, hat kein
          * 5-Prozent-Niveau mehr, sondern eher 19 bis 20. Das Beenden bleibt
          * moeglich, aber die Entscheidung dazu faellt blind, und genau das ist
          * der Punkt. */}
        <RunningProgress
          results={results}
          startedAt={latestExperiment.started_at}
          plannedEndAt={latestExperiment.planned_end_at}
          t={t}
          lang={lang}
        />

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm">{t('experiment.running.finishEarlyCta')}</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('experiment.running.finishEarlyCta')}</AlertDialogTitle>
              <AlertDialogDescription>{t('experiment.running.finishEarlyConfirm')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('experiment.start.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleComplete}>
                {t('experiment.running.finishEarlyConfirmCta')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  } else if (latestExperiment?.status === 'completed') {
    body = (
      <div className="space-y-4">
        {results && <ResultsSummary results={results} t={t} lang={lang} />}
        <p className="text-xs text-muted-foreground">{t('experiment.results.rolloutHint')}</p>
        <Collapsible open={methodOpen} onOpenChange={setMethodOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="group flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('experiment.results.methodTitle')}
              <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 pt-2 text-xs text-muted-foreground">
            <p>{t('experiment.results.methodBody')}</p>
            {/* Nur wenn gesetzt: Altbestand ist vor der Pre-Registration gestartet,
              * ein Platzhalter waere dort eine Behauptung ohne Deckung. */}
            {results?.preregistration_ref && (
              <p>{t('experiment.results.preregistration', { ref: results.preregistration_ref })}</p>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  } else {
    // Neither running nor completed: no experiment yet, or the last one was
    // cancelled. Silent degrade if eligibility itself failed to load (network
    // error) — nothing broken to show, so nothing renders.
    if (!eligibility) {
      body = null;
    } else {
      body = (
        <EligibilityBody
          eligibility={eligibility}
          wasCancelled={latestExperiment?.status === 'cancelled'}
          isStarting={isStarting}
          autoUploadEnabled={autoUploadEnabled}
          onStart={handleStart}
          onNavigateWorkbench={() => navigate('/workbench')}
          t={t}
          lang={lang}
        />
      );
    }
  }

  if (body === null) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-primary" />
          {t('experiment.title')}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t('experiment.subtitle')}</p>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}

/** Reihenfolge nach Dringlichkeit, identisch zur Prioritaet in der RPC. Steuert
 * die Aufzaehlung der uebrigen offenen Punkte. */
const GATE_ORDER: ExperimentGate[] = [
  'running', 'mc_access', 'approval', 'population', 'clicks', 'mde_unknown', 'mde',
];

/** Genaue Stueckzahlen statt der K/M-Kurzform: "10.756 Produkte" traegt die
 * Entscheidung, "10.8K Produkte" nicht. */
function formatCount(n: number | null | undefined, lang: Lang): string {
  if (n === null || n === undefined) return '0';
  return formatInteger(n, lang);
}

function formatMde(v: number | null | undefined, lang: Lang): string {
  return formatDecimal(v, lang);
}

/** Hauptmeldung zum wichtigsten scheiternden Gate. */
function gateMessage(
  gate: ExperimentGate,
  e: ExperimentEligibility,
  t: (key: string, opts?: Record<string, unknown>) => string,
  lang: Lang,
): string | null {
  switch (gate) {
    case 'running':
      return t('experiment.notEligible.running');
    case 'mc_access':
      return t('experiment.notEligible.mcAccess');
    case 'approval':
      // Der Fall, der vorher faelschlich als "Feed zu klein" gelesen wurde: die
      // Produkte sind fertig, es fehlt nur die Freigabe.
      return t('experiment.notEligible.approval', {
        ready: formatCount(e.completed_not_uploaded, lang),
        pending: formatCount(e.pending_approval, lang),
      });
    case 'population':
      return t('experiment.notEligible.population', { products: formatCount(e.population_count, lang) });
    case 'clicks':
      return t('experiment.notEligible.clicks', { clicks: formatCount(e.pre_clicks, lang) });
    case 'mde_unknown':
      return t('experiment.notEligible.mdeUnknown');
    case 'mde':
      return t('experiment.notEligible.mde', { mde: formatMde(e.mde_rel_pct, lang) });
    default:
      return null;
  }
}

/** Kurzform fuer die Aufzaehlung der uebrigen offenen Punkte. */
function gateShortLabel(
  gate: ExperimentGate,
  e: ExperimentEligibility,
  t: (key: string, opts?: Record<string, unknown>) => string,
  lang: Lang,
): string {
  switch (gate) {
    case 'running':
      return t('experiment.notEligible.short.running');
    case 'mc_access':
      return t('experiment.notEligible.short.mcAccess');
    case 'approval':
      return t('experiment.notEligible.short.approval', { pending: formatCount(e.pending_approval, lang) });
    case 'population':
      return t('experiment.notEligible.short.population', { products: formatCount(e.population_count, lang) });
    case 'clicks':
      return t('experiment.notEligible.short.clicks', { clicks: formatCount(e.pre_clicks, lang) });
    case 'mde_unknown':
      return t('experiment.notEligible.short.mdeUnknown');
    case 'mde':
      return t('experiment.notEligible.short.mde', { mde: formatMde(e.mde_rel_pct, lang) });
    default:
      return '';
  }
}

interface EligibilityBodyProps {
  eligibility: ExperimentEligibility;
  wasCancelled: boolean;
  isStarting: boolean;
  /** Mindestens ein Auto-Upload-Schalter des Nutzers ist an. */
  autoUploadEnabled: boolean;
  onStart: () => void;
  onNavigateWorkbench: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
  lang: Lang;
}

function EligibilityBody({ eligibility, wasCancelled, isStarting, autoUploadEnabled, onStart, onNavigateWorkbench, t, lang }: EligibilityBodyProps) {
  const cancelledNotice = wasCancelled && (
    <p className="text-xs text-muted-foreground">{t('experiment.cancelled')}</p>
  );

  if (eligibility.eligible) {
    return (
      <div className="space-y-3">
        {cancelledNotice}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" disabled={isStarting}>{t('experiment.start.cta')}</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('experiment.start.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <span className="block space-y-2 text-sm text-muted-foreground">
                  <span className="block">
                    {t('experiment.start.population', { count: eligibility.population_count, formatted: formatInteger(eligibility.population_count, lang) })}
                  </span>
                  <span className="block">{t('experiment.start.mechanics')}</span>
                  <span className="block">
                    {t('experiment.start.detectable', { mde: formatMde(eligibility.mde_rel_pct, lang) })}
                  </span>
                  <span className="block font-medium text-foreground">{t('experiment.start.caveat')}</span>
                  <span className="block font-medium text-foreground">{t('experiment.start.controlNote')}</span>
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('experiment.start.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={onStart} disabled={isStarting}>
                {t('experiment.start.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // failing_gates ist der vollstaendige Befund, failing_gate nur das wichtigste.
  // Der Fallback deckt den Moment ab, in dem das Frontend schon neu ist und die
  // RPC noch nicht (Deploy-Reihenfolge Migration -> Frontend).
  const gates: ExperimentGate[] = eligibility.failing_gates?.length
    ? eligibility.failing_gates
    : eligibility.failing_gate
      ? [eligibility.failing_gate]
      : [];

  const primary = eligibility.failing_gate ?? gates[0] ?? null;
  const secondary = GATE_ORDER.filter((g) => g !== primary && gates.includes(g));

  const message = primary ? gateMessage(primary, eligibility, t, lang) : null;
  const potentialMde = eligibility.mde_potential_pct ?? null;

  // Der Link gehoert zum Approval-Gate. Ist das Gate das wichtigste, steht er
  // direkt bei der Meldung; ist es nur einer von mehreren offenen Punkten,
  // steht er unter der Aufzaehlung, damit er nicht kontextlos in der Luft haengt.
  const workbenchLink = gates.includes('approval') ? (
    <Button variant="link" size="sm" className="h-auto p-0 gap-1" onClick={onNavigateWorkbench}>
      {t('experiment.notEligible.workbenchLink')}
      <ArrowRight className="h-3 w-3" />
    </Button>
  ) : null;

  return (
    <div className="space-y-2">
      {cancelledNotice}
      {message && <p className="text-sm text-muted-foreground">{message}</p>}

      {/* Die Nachweisgrenze nach vollstaendiger Freigabe ist die Zahl, die die
        * Entscheidung traegt: sie beziffert, was das Freigeben ueberhaupt
        * einbringt. Ohne sie ist "gib frei" eine Aufforderung ins Blaue. */}
      {primary === 'approval' && potentialMde !== null && (
        <p className="text-sm text-muted-foreground">
          {t('experiment.notEligible.approvalPotential', { mde: formatMde(potentialMde, lang) })}
        </p>
      )}

      {primary === 'approval' && workbenchLink}

      {/* Die Kehrseite der Automatik-Gabel. Ohne diesen Satz liest sich das
        * Population-Gate als "Feed zu klein", waehrend in Wahrheit der
        * Auto-Upload jedes optimierte Produkt sofort auf `uploaded` setzt und
        * damit aus der Testpopulation nimmt. */}
      {gates.includes('population') && autoUploadEnabled && (
        <p className="text-xs text-muted-foreground">{t('experiment.notEligible.autoUploadReason')}</p>
      )}

      {/* Wer drei Dinge erledigen muss, soll das auf einen Blick sehen und nicht
        * dreimal nacheinander gegen dieselbe Wand laufen. */}
      {secondary.length > 0 && (
        <div className="space-y-1 pt-1">
          <p className="text-xs font-medium text-foreground">{t('experiment.notEligible.alsoOpen')}</p>
          <ul className="list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
            {secondary.map((g) => (
              <li key={g}>{gateShortLabel(g, eligibility, t, lang)}</li>
            ))}
          </ul>
        </div>
      )}

      {primary !== 'approval' && workbenchLink}
    </div>
  );
}
